import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { kickChannel } from "../mock";
import { useKickStatus } from "../hooks/useKickStatus";

const NavLink = ({ to, label, badge, active, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`relative uppercase text-[13px] font-bold tracking-widest transition-colors duration-200 ${
      active ? "text-[#3ddc84]" : "text-[#97a1b8] hover:text-white"
    }`}
  >
    {label}
    {badge && (
      <span className="absolute -top-2.5 -right-7 text-[9px] font-extrabold px-1.5 py-0.5 rounded bg-[#3ddc84] text-[#0d1420] leading-none">
        {badge}
      </span>
    )}
    {active && (
      <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rotate-45 bg-[#3ddc84]" />
    )}
  </Link>
);

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { isLive } = useKickStatus();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn, { passive: true });
    fn();
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const goBonuses = (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById("bonuses")?.scrollIntoView({ behavior: "smooth" });
      }, 350);
    } else {
      document.getElementById("bonuses")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#171b26]/95 backdrop-blur-md shadow-[0_4px_24px_rgba(0,0,0,0.45)]"
          : "bg-[#171b26]/60 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[68px] flex items-center justify-between">
        <Link to="/" className="shrink-0" data-testid="nav-logo">
          <Logo size="md" />
        </Link>

        {/* desktop nav */}
        <nav className="hidden md:flex items-center gap-9" data-testid="nav-links">
          <NavLink to="/" label="Home" active={location.pathname === "/"} />
          <NavLink
            to="/leaderboard"
            label="Leaderboard"
            active={location.pathname === "/leaderboard"}
          />
          <a
            href="#bonuses"
            onClick={goBonuses}
            className="relative uppercase text-[13px] font-bold tracking-widest text-[#97a1b8] hover:text-white transition-colors duration-200"
            data-testid="nav-claim-bonus"
          >
            Claim Bonus
            <span className="absolute -top-2.5 -right-7 text-[9px] font-extrabold px-1.5 py-0.5 rounded bg-[#3ddc84] text-[#0d1420] leading-none">
              NEW
            </span>
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={kickChannel.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-extrabold uppercase tracking-widest transition-colors duration-200 ${
              isLive
                ? "bg-[#3ddc84]/90 text-[#0d1420]"
                : "bg-[#12151f]/85 text-[#97a1b8] border border-[#2c3448]"
            }`}
            data-testid="nav-live-badge"
          >
            <span
              className={`w-2 h-2 rounded-full ${
                isLive ? "bg-[#0d1420] animate-pulse" : "bg-[#5d6579]"
              }`}
            />
            {isLive ? "Live" : "Offline"}
          </a>
          <button
            className="md:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* mobile menu */}
      {open && (
        <div className="md:hidden bg-[#1b2030] border-t border-[#2a3145] px-6 py-5 flex flex-col gap-5">
          <NavLink to="/" label="Home" active={location.pathname === "/"} onClick={() => setOpen(false)} />
          <NavLink
            to="/leaderboard"
            label="Leaderboard"
            active={location.pathname === "/leaderboard"}
            onClick={() => setOpen(false)}
          />
          <a
            href="#bonuses"
            onClick={goBonuses}
            className="uppercase text-[13px] font-bold tracking-widest text-[#97a1b8]"
          >
            Claim Bonus
          </a>
          <a
            href={kickChannel.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-[11px] font-extrabold uppercase tracking-widest ${
              isLive
                ? "bg-[#3ddc84]/90 text-[#0d1420]"
                : "bg-[#12151f]/85 text-[#97a1b8] border border-[#2c3448]"
            }`}
            data-testid="nav-live-badge-mobile"
          >
            <span
              className={`w-2 h-2 rounded-full ${
                isLive ? "bg-[#0d1420] animate-pulse" : "bg-[#5d6579]"
              }`}
            />
            {isLive ? "Live" : "Offline"}
          </a>
        </div>
      )}
    </header>
  );
};

export default Navbar;
